<template>
	<div>
		<v-row v-if="(group || {}).hybrid_appointments_enabled || false">
			<v-col class="pt-0 pl-2">
				<v-btn-toggle
					class="d-flex flex-row hybrid-btn-toggle mr-2"
					v-model="preferredAssetType"
					divided
					mandatory
					color="blue-grey"
					variant="outlined"
				>
					<v-btn size="large" value="INPERSON" class="flex-grow-1">
						<span class="dot inperson-asset"></span>
						In Person
					</v-btn>
					<v-btn size="large" value="ONLINE" class="flex-grow-1">
						<span class="dot online-asset"></span> Virtual
					</v-btn>
				</v-btn-toggle>
			</v-col>
		</v-row>

		<!-- Show loading message while fetching slots -->
		<div v-if="isLoading" class="display-1 text-center unavailable-message">
			<p>Loading slots...</p>
		</div>

		<div
			v-if="!isLoading && group && slots.length === 0"
			class="display-1 text-center unavailable-message"
		>
			{{ this.formatUnavailableMessage }}
		</div>

		<div v-if="!isLoading && group && slots.length > 0">
			<v-row
				v-if="group.list_assets && slots.length > 0"
				class="text-center"
			>
				<v-col :cols="3">
					<div
						class="caption text-no-wrap my-1"
						v-for="(slot, index) in slots"
						:key="index"
					>
						{{ slotLabel(slot) }}
					</div>
				</v-col>
				<v-col
					class="pa-0"
					:cols="Math.floor(9 / group.assets.length)"
					v-for="(asset, index) in group.assets"
					:key="index"
				>
					<div class="overline">{{ asset.name }}</div>
					<co-asset-availability
						:blockSize="blockSize"
						:availability="
							trimRange(asset.availability[selectedDay])
						"
						:range="range"
					>
					</co-asset-availability>
				</v-col>
			</v-row>
			<v-row v-else>
				<v-col
					class="booking-row frame mr-1 ml-3 pa-0 appointments-column"
					:class="{ landscape: windowWidth > 600 }"
				>
					<v-btn
						@click="selectSlot(slot)"
						outlined
						block
						v-for="(slot, index) in slots"
						:key="index"
						color="blue-grey lighten-1"
						class="mb-4 appointment-slot"
						dark
						>{{ slotLabel(slot) }}</v-btn
					>
				</v-col>
			</v-row>
		</div>
	</div>
</template>

<script>
import CoAssetAvailability from './asset-availability.vue';
import moment from 'moment';
export default {
	props: {
		id: { type: Number, required: true },
		multiplier: { type: Number, required: true }
	},
	components: {
		CoAssetAvailability
	},
	data() {
		return {
			windowWidth: 0,
			preferredAssetType: 'INPERSON'
		};
	},
	watch: {
		id: {
			immediate: true,
			handler(id) {
				let assetType = this.preferredAssetType;
				this.$store.dispatch('fetchAssetGroup', {
					id,
					date: this.selectedDate,
					assetType: assetType,
					multiplier: this.multiplier
				});
			}
		},
		selectedDate: {
			immediate: true,
			handler(date) {
				let assetType = this.preferredAssetType;
				this.$store.dispatch('fetchAssetGroup', {
					id: this.id,
					date,
					assetType: assetType,
					multiplier: this.multiplier
				});
			}
		},
		preferredAssetType: {
			immediate: true,
			handler(_) {
				let assetType = this.preferredAssetType;
				this.$store.dispatch('fetchAssetGroup', {
					id: this.id,
					date: this.selectedDate,
					assetType: assetType,
					multiplier: this.multiplier
				});
			}
		},
		multiplier: {
			immediate: true,
			handler(_) {
				this.$store.dispatch('fetchAssetGroup', {
					id: this.id,
					date: this.selectedDate,
					assetType: this.preferredAssetType,
					multiplier: this.multiplier
				});
			}
		}
	},
	created() {
		this.keyword = window.config.keyword;
	},
	mounted() {
		this.$store.dispatch('fetchClientSettings');
		this.$store.dispatch('fetchAssetGroup', {
			id: this.id,
			date: this.selectedDate,
			assetType: this.preferredAssetType,
			multiplier: this.multiplier
		});

		window.addEventListener('resize', () => {
			this.windowWidth = window.innerWidth;
		});
	},
	methods: {
		selectSlot(slot) {
			const slotNumber = slot / this.blockSize;
			const asset = this.group.assets.find(
				asset => asset.availability[this.selectedDay][slotNumber]
			);

			// Scroll to top of the screen
			window.scrollTo({ top: 0, behavior: 'smooth' });

			this.$router.push({
				name: 'bookasset',
				params: { id: asset.id, slot: slotNumber },
				query: {
					assetType: this.preferredAssetType,
					multiplier: this.multiplier
				}
			});
		},
		trimRange(slots) {
			return slots.slice(
				this.range.start / this.blockSize + 1,
				this.range.end / this.blockSize - 1
			);
		},
		slotLabel(slot) {
			return moment()
				.utc()
				.utcOffset(this.libraryTimezone)
				.startOf('days')
				.add(slot, 'minutes')
				.format('hh:mm a');
		}
	},
	computed: {
		slots() {
			return this.group.assets.reduce((ret, asset) => {
				// Prevent bookings today, if selected
				if (this.preventSameDayBooking && this.selectedDayIsToday)
					return [];
				for (
					let i = 0;
					i < asset.availability[this.selectedDay].length;
					i++
				) {
					if (
						asset.availability[this.selectedDay][i] &&
						!ret.includes(i * this.blockSize)
					) {
						if (i * this.blockSize > this.slotsPassedToday) {
							ret.push(i * this.blockSize);
						}
					}
				}
				return ret.sort((a, b) => a - b);
			}, []);
		},
		range() {
			return this.group.assets.reduce(
				(ret, asset) => {
					const firstOpen = asset.availability[
						this.selectedDay
					].indexOf(true);
					const lastOpen = asset.availability[
						this.selectedDay
					].lastIndexOf(true);
					return {
						start: Math.max(ret.start, firstOpen * this.blockSize),
						end: Math.min(lastOpen * this.blockSize)
					};
				},
				{ start: 0, end: 1440 }
			);
		},
		bookingSettings() {
			return {
				booking_cutoff_time: this.group.booking_cutoff_time,
				prevent_same_day_booking: this.group.prevent_same_day_booking,
				prevent_booking_more_than_days: this.group.booking_days_advance
			};
		},
		group() {
			return this.$store.getters.getAssetGroupForId(this.id);
		},
		clientSettings() {
			return this.$store.state.clientDetails;
		},
		blockSize() {
			return this.group.block_size;
		},
		bookingCutoffTime() {
			return this.group.booking_cutoff_time;
		},
		preventSameDayBooking() {
			return this.group.prevent_same_day_booking;
		},
		selectedDate() {
			return this.$store.state.selectedDate;
		},
		isLoading() {
			return this.$store.state.isLoading;
		},
		selectedDay() {
			return moment(this.selectedDate).format('d');
		},
		selectedDayIsToday() {
			return (
				this.selectedDate ==
				moment()
					.utc()
					.utcOffset(this.libraryTimezone)
					.startOf('day')
					.format('YYYY-MM-DD')
			);
		},
		libraryTimezone() {
			return this.clientSettings.timezone;
		},
		slotsPassedToday() {
			const bookingCutoffTimeMinutes = this.bookingCutoffTime;
			// Treat the library timezone as UTC to allow us to compare days
			const now = moment()
				.utc()
				.add(this.libraryTimezone, 'hour');
			const startOfToday = moment(now).startOf('day');
			const selectedDay = moment(this.selectedDate, 'YYYY-MM-DD');
			const cutoff = moment(now).add(bookingCutoffTimeMinutes, 'minutes');
			if (selectedDay.isSame(now, 'day')) {
				// Cut-off occurs within Today
				return (
					now.diff(startOfToday, 'minutes') + bookingCutoffTimeMinutes
				);
			} else if (selectedDay.isSame(cutoff, 'day')) {
				// Cut-off occurs on selected, non-today, day
				return cutoff.hour() * 60 + cutoff.minute();
			} else if (selectedDay.isBefore(cutoff, 'day')) {
				// Cutoff occurs after the selected day, therefore return 1440 to indicate all slots are unavailable for this day
				return 1440;
			}
			return 0;
		},
		formatUnavailableMessage() {
			if (this.getExceptionMessage) {
				return `${this.$store.state.clientSettings.appointment_exception_message} ${this.getExceptionMessage}`;
			} else {
				if (this.multiplier > 1) {
					return 'Sorry, there is no availability for the selected appointment type.';
				} else if (this.group.hybrid_appointments_enabled) {
					const messages = {
						INPERSON:
							'Sorry, there are no in-person appointments available.',
						ONLINE:
							'Sorry, there are no virtual appointments available.'
					};
					return messages[this.preferredAssetType];
				} else {
					if (this.multiplier > 1) {
						return 'Sorry, there is no availability for the selected appointment type.';
					} else {
						return this.selectedDayIsToday
							? `There is no remaining availability today, please select a later date.`
							: `Sorry, there is no availability on the selected date.`;
					}
				}
			}
		},
		formatUnavailableMessageDetail() {
			if (this.getExceptionMessage) {
				return `${this.getExceptionMessage}`;
			} else {
				return '';
			}
		},
		getExceptionMessage() {
			try {
				return this.group.exceptions !== undefined &&
					this.group.exceptions[this.selectedDate]
					? this.group.exceptions[this.selectedDate]
					: false;
			} catch (e) {
				console.error(
					`Unable to determine exception reason for ${this.selectedDate}`,
					e
				);
				return false;
			}
		}
	}
};
</script>

<style lang="scss">
$padding: 5px;
$radius: 9px;
$button-height: 56px;
$text-height: 20px;

#app {
	.v-select {
		button[aria-label~='clear'].v-icon,
		.v-input__icon.v-input__icon--append i.v-icon,
		.v-label {
			color: #0ea9bd !important;
			font-size: 16px;
		}
	}

	.date-picker {
		margin-top: 30px;

		.v-date-picker-title {
			color: black;
		}
	}

	.hybrid-btn-toggle {
		font-size: smaller;
		border-radius: $radius;
		border: 1px solid var(--medium-grey) 0%;
		margin-bottom: 16px;

		button.v-btn:first-child {
			width: 50%;
			border-right: none;
			padding-left: 7px;
			padding-right: 0;
			margin-left: 0px;

			.dot {
				vertical-align: middle;
			}
		}

		button.v-btn.v-item--active:first-child {
			padding-left: 7px;
			padding-right: 0;

			.v-btn__content {
				margin-right: $padding;
			}
		}

		button[size='large'].v-btn {
			height: $button-height;
			vertical-align: middle;
		}

		button.v-btn,
		button.v-item--active {
			opacity: 1;
			padding: $padding;
			background-color: #f2f2f2;

			.v-btn__content {
				padding-top: (($button-height - $text-height) / 2) - $padding;
				padding-bottom: (($button-height - $text-height) / 2) - $padding;
				padding-left: $padding;
				padding-right: $padding;
				vertical-align: middle;
			}
		}

		button.v-item--active::before {
			background-color: #f2f2f2;
		}

		button[size='large'].v-item--active {
			background-color: #f2f2f2;

			.v-btn__content {
				display: flex;
				background-color: black;
				border-radius: $radius;
				color: white;

				.v-icon {
					color: white;
				}
			}
		}
	}

	.booking-row {
		button.v-btn.appointment-slot {
			border-radius: $radius !important;
		}
	}
}
</style>

<style lang="scss" scoped>
$radius: 9px;

.inperson-asset {
	background-color: #0ea9bd !important;
	border-color: #0ea9bd !important;
}

button[value='ONLINE'] .dot.online-asset {
	background-color: transparent !important;
	border: 2px solid #2a9d4c !important;
}

.booking-row {
	max-height: 575px;
	overflow-x: hidden;
	overflow-y: scroll;

	button.appointment-slot {
		border-color: #0ea9bd !important;
		color: #0ea9bd !important;
		border-radius: $radius !important;
	}
}

.frame::-webkit-scrollbar {
	-webkit-appearance: none;
}

.frame::-webkit-scrollbar:vertical {
	width: 11px;
}

.frame::-webkit-scrollbar:horizontal {
	height: 11px;
}

.frame::-webkit-scrollbar-thumb {
	border-radius: 8px;
	border: 2px solid white;
	/* should match background, can't be transparent */
	background-color: rgba(0, 0, 0, 0.5);
}

.frame::-webkit-scrollbar-track {
	background-color: #fff;
	border-radius: 8px;
}

.dot {
	height: 15px;
	width: 15px;
	background-color: #bbb;
	border-radius: 50%;
	display: inline-block;
	margin-right: 5px;
}

.v-application .unavailable-message {
	font-size: medium !important;
}

.appointments-column.landscape {
	max-height: 490px;
}
</style>
